import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface ImportTemplatesListProps {
  service: TableService;
  role?: 'admin' | 'operator';
}

interface ImportTemplateRow {
  id: string;
  name: string;
  import_type?: string;
  column_mappings_count?: number;
  default_values_count?: number;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

const ImportTemplatesList: React.FC<ImportTemplatesListProps> = ({
  service,
  role = 'admin',
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'shared.import_templates.list.columns.name',
      persist: true,
      sortable: true,
      sortFieldName: 'import_templates.name',
      wrap: true,
      cell: ({ id, name }: ImportTemplateRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/import_templates/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/${role}/import_templates/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: 'shared.import_templates.list.columns.import_type',
      sortable: true,
      sortFieldName: 'import_templates.import_type',
      wrap: true,
      cell: ({ import_type }: ImportTemplateRow) => (
        <Tag color="blue">
          {t(`common.import_type.${import_type}`)}
        </Tag>
      ),
    },
    {
      nameKey: 'shared.import_templates.list.columns.column_mappings_count',
      sortable: false,
      width: '120px',
      wrap: true,
      selector: ({ column_mappings_count }: ImportTemplateRow) => column_mappings_count,
    },
    {
      nameKey: 'shared.import_templates.list.columns.default_values_count',
      sortable: false,
      width: '120px',
      wrap: true,
      selector: ({ default_values_count }: ImportTemplateRow) => default_values_count,
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'import_templates.created_at',
      width: '180px',
      wrap: true,
      selector: ({ created_at }: ImportTemplateRow) => created_at,
    },
    {
      nameKey: 'common.updated_at',
      sortable: true,
      sortFieldName: 'import_templates.updated_at',
      width: '180px',
      wrap: true,
      selector: ({ updated_at }: ImportTemplateRow) => updated_at,
    },
  ], [role, t])

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('shared.import_templates.list.filters.import_type'),
      component: MultipleValue,
      inputName: 'import_templates.import_type',
      defaultPinned: true,
      createable: false,
      defaultOptions: [
        { label: t('common.import_type.order'), value: 'order' },
        { label: t('common.import_type.product'), value: 'product' },
        { label: t('common.import_type.inbound'), value: 'inbound' },
        { label: t('common.import_type.return_order'), value: 'return_order' },
        { label: t('common.import_type.contact_address'), value: 'contact_address' },
        { label: t('common.import_type.bundle'), value: 'bundle' },
        { label: t('common.import_type.shelf'), value: 'shelf' },
        { label: t('common.import_type.stock_adjustment'), value: 'stock_adjustment' },
        { label: t('common.import_type.inventory'), value: 'inventory' },
      ],
    },
    {
      label: t('shared.import_templates.list.filters.name'),
      component: MultipleValue,
      inputName: 'import_templates.name',
      createable: true,
    },
  ], [t])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [t('shared.import_templates.list.columns.name')],
      }}
      columnsEditable={true}
      tableId="AdminImportTemplatesTable"
      defaultQuery={{
        sorting_field: 'import_templates.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.import_templates.list.filter_help_example') as string}
    />
  )
}

export default ImportTemplatesList
